/* eslint-disable @typescript-eslint/naming-convention */
import { FundPerformance } from 'src/app/domain/fund-performance/dto/FundPerformance';
import { getCell, formats } from '../../utils';
import { Rows } from '../../interfaces';
import { getFpHeadersForLogic } from './headers';


export const getNewMapping = (fp: FundPerformance): object => {
  const body = {
    Period: fp.period,
    Commitment: getCell({
      v: roundedZeroToUndefined(fp.totalCommitment),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Contribution received %': getCell({
      f: `IFERROR(E${currentRowIndex}/B${currentRowIndex},0)`,
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    'Contribution received $': getCell({
      v: roundedZeroToUndefined(fp.contribution),
      z: formats.thousandsSeparator, t: 'n',
    }),
    ' Net gain / (loss)': getCell({
      f: `F${currentRowIndex}+G${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV & distribution': getCell({
      f: `E${currentRowIndex}+F${currentRowIndex}+G${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Portfolio gain / (loss)': getCell({
      v: roundedZeroToUndefined(fp.totalFmv - fp.totalCost),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Aggregated organizational expenses': getCell({
      v: roundedZeroToUndefined(fp.aggregatedOrgExpenses),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Total expenses': getCell({ f: `SUM(Y${currentRowIndex}:AA${currentRowIndex})`,
      z: formats.thousandsSeparator,
      t: 'n' }),
    'Management fees': getCell({
      v: roundedZeroToUndefined(fp.mgmFees),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Other OpEx': getCell({
      v: roundedZeroToUndefined(fp.totalExpenses - fp.mgmFees - (fp.aggregatedOrgExpenses !== null ?
        Math.round(fp.aggregatedOrgExpenses) : 0)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Total loss from operations': getCell({
      f: `SUM(Y${currentRowIndex}:AA${currentRowIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'TVPI': getCell({
      f: `IFERROR(H${currentRowIndex}/E${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR': getCell({
      v: zeroToUndefined(fp.netIrr ?? fp.irrCalculated),
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    'Total carried interest': getCell({
      v: roundedZeroToUndefined(fp.carryInterest),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Realized carried interest': getCell({
      v: roundedZeroToUndefined(fp.carryDistribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unrealized carried interest': getCell({
      f: `AC${currentRowIndex}-AD${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain / (loss) after carry': getCell({
      f: `O${currentRowIndex}+AC${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'TVPI (after carry)': getCell({
      f: `IFERROR(SUM(AF${currentRowIndex},E${currentRowIndex})/E${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'DPI (after carry)': getCell({
      f: `IFERROR(-K${currentRowIndex}/E${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'RVPI (after carry)': getCell({
      f: `IFERROR(SUM(J${currentRowIndex},AE${currentRowIndex})/E${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR (after carry)': getCell({
      v: zeroToUndefined(fp.netIrrAfterCarry ?? fp.irrCalculatedAfterCarry),
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    'Portfolio MOIC': getCell({
      v: zeroToUndefined(fp.totalFmv / fp.totalCost),
      z: formats.multiplier,
      t: 'n',
    }),
    'Portfolio IRR': getCell({
      v: zeroToUndefined(fp.calculatedGrossIrr),
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    'LP distribution': getCell({
      v: roundedZeroToUndefined(fp.distribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Carry distribution': getCell({
      v: roundedZeroToUndefined(fp.carryDistribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Total distribution': getCell({
      f: `SUM(K${currentRowIndex}:L${currentRowIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    DPI: getCell({
      f: `IFERROR(-M${currentRowIndex}/E${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    NAV: getCell({
      f: `H${currentRowIndex}+M${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    RVPI: getCell({
      f: `IFERROR(J${currentRowIndex}/E${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    Comment: fp.comment,
    Prediction: fp.isBasedOnPrognosis ? 'Yes' : 'No',
    empty: null,
    'empty(1)': null,
    'empty(2)': null,
    'empty(3)': null,
    'empty(4)': null,
    'empty(5)': null,
  };
  currentRowIndex++;
  return body;
};

export const roundedZeroToUndefined = (v: number): string | undefined => {
  if (v) return v.toFixed(0);
  return undefined;
};

export const zeroToUndefined = (v: number): number | undefined => {
  if (v) return v;
  return undefined;
};

export const getManyRows = (fundPerformances: FundPerformance[], headers: string[]): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = fundPerformances.map((d) => getNewMapping(d));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(mapped[header]);
    }
    rows.push(row);
  };
  return rows;
};

export const getPreparedData = (fundPerformances: FundPerformance[], headers: string[]): Rows => {
  const newData: Rows = [];
  newData.push(headers);
  const headersForLogic = getFpHeadersForLogic();
  const rows = getManyRows(fundPerformances, headersForLogic);
  newData.push(...rows);
  resetIndex();
  return newData;
};
// set current row indicator to start position
const resetIndex = (): void => {
  currentRowIndex = 2;
};
// current row indicator
let currentRowIndex = 2;

